import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn, formatNumber } from '@/lib/utils'
import { LANGUAGES } from '@/types/terms'
import { DocumentFilterSection, type DocumentFilter } from './document-filter-section'

export type ConceptSort = 'terms' | 'gaps' | 'alpha'

interface ConceptFiltersPanelProps {
  lang: string
  /** Language a concept must be MISSING — the gap finder. */
  missingLang: string
  domain: string
  sort: ConceptSort
  domains: string[]
  shown: number
  total: number
  /** Selected documents narrowing this list; null when none. */
  documentFilter?: DocumentFilter | null
  onChange: (patch: { lang?: string; missingLang?: string; domain?: string }) => void
  onSortChange: (s: ConceptSort) => void
  onClear: () => void
}

/**
 * Filters for the concept list.
 *
 * Deliberately not a copy of the term filters. The question a terminologist
 * brings to a concept list is not "which terms are frequent" but "where are
 * the holes" — which concepts have no Finnish equivalent yet, which cover all
 * three languages. So the panel is built around language coverage, and the
 * missing-language control is the one that turns this list into a worklist.
 */
export function ConceptFiltersPanel({
  lang,
  missingLang,
  domain,
  sort,
  domains,
  shown,
  total,
  documentFilter,
  onChange,
  onSortChange,
  onClear,
}: ConceptFiltersPanelProps) {
  const { t } = useTranslation()
  const [domainFilter, setDomainFilter] = useState('')

  const visibleDomains = useMemo(() => {
    const f = domainFilter.trim().toLowerCase()
    const list = f ? domains.filter((d) => d.toLowerCase().includes(f)) : domains
    return list.slice(0, 200)
  }, [domains, domainFilter])

  const hasFilters = !!(lang || missingLang || domain)

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto scrollbar-thin p-3">
      <DocumentFilterSection filter={documentFilter} />
      <div>
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('filters.hasLanguage')}
        </div>
        <ToggleGroup
          type="single"
          value={lang || 'all'}
          onValueChange={(v) => onChange({ lang: !v || v === 'all' ? '' : v })}
          className="w-full"
        >
          <ToggleGroupItem value="all" className="flex-1">
            {t('common.all')}
          </ToggleGroupItem>
          {LANGUAGES.map((l) => (
            <ToggleGroupItem key={l} value={l} className="flex-1 uppercase">
              {l}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div>
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('filters.missingLanguage')}
        </div>
        <ToggleGroup
          type="single"
          value={missingLang || 'none'}
          onValueChange={(v) => onChange({ missingLang: !v || v === 'none' ? '' : v })}
          className="w-full"
        >
          <ToggleGroupItem value="none" className="flex-1">
            {t('common.none')}
          </ToggleGroupItem>
          {LANGUAGES.map((l) => (
            <ToggleGroupItem key={l} value={l} className="flex-1 uppercase">
              {l}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
          {t('filters.missingLanguageHint')}
        </p>
      </div>

      <div>
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('common.sortBy')}
        </div>
        <ToggleGroup
          type="single"
          value={sort}
          onValueChange={(v) => v && onSortChange(v as ConceptSort)}
          className="w-full"
        >
          <ToggleGroupItem value="terms" className="flex-1">
            {t('filters.sortSize')}
          </ToggleGroupItem>
          <ToggleGroupItem value="gaps" className="flex-1">
            {t('filters.sortGaps')}
          </ToggleGroupItem>
          <ToggleGroupItem value="alpha" className="flex-1">
            {t('common.alphabetical')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('filters.domain')}
          </span>
          {domain && (
            <Button variant="ghost" size="icon-sm" onClick={() => onChange({ domain: '' })}>
              <X />
            </Button>
          )}
        </div>
        <div className="relative mb-1.5">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            placeholder={t('filters.searchDomain')}
            className="h-7 pl-7 text-xs"
          />
        </div>
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto rounded-md border border-border">
          {visibleDomains.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange({ domain: d === domain ? '' : d })}
              className={cn(
                'block w-full truncate px-2 py-1 text-left text-[11px] transition-colors',
                d === domain
                  ? 'bg-accent font-medium text-accent-foreground'
                  : 'hover:bg-muted',
              )}
            >
              {d}
            </button>
          ))}
          {!visibleDomains.length && (
            <p className="px-2 py-1 text-[11px] text-muted-foreground">
              {t('common.noMatchesFound')}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
        <span>
          {formatNumber(shown)} / {formatNumber(total)}
        </span>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('common.clearFilters')}
          </Button>
        )}
      </div>
    </div>
  )
}
